import { AUDIT_ACTIONS, type AuditEntityType, canEditAnySubmission } from '@cult/domain';
import type { AuditLogEntry, AuditValue } from '@cult/schemas';
import { Skeleton, Tag, Tooltip, Typography } from 'antd';
import dayjs from 'dayjs';
import { BlankState, InlineError, UserAvatar } from '../../../../components';
import { useAuditLog } from '../../hooks';
import styles from './EditHistory.module.scss';

const { Text, Paragraph } = Typography;

const FIELD_LABELS: Record<string, string> = {
  title: 'Title',
  body: 'Description',
  problem: 'What to fix or improve',
  solution: 'What we should do',
  categoryId: 'Category',
  frequency: 'Frequency',
  reach: 'Reach',
  impact: 'Impact',
  decisionNote: 'Decision note',
  receiver: 'Recipient',
  coreValues: 'Core values',
  frameworks: 'Frameworks',
  behavior: 'Behavior',
  whatHappened: 'What happened',
  attachments: 'Attachments',
};

// Free-text fields get before/after blocks instead of an inline "old → new".
const LONG_TEXT_FIELDS = new Set(['body', 'problem', 'solution', 'whatHappened', 'decisionNote', 'behavior']);

type Props = {
  entityType: AuditEntityType;
  entityId: string;
  /** The record's author — an admin entry by anyone else is tagged as an admin edit. */
  authorId: string | null;
};

/** Admin-only journal of every edit to an idea, praise or direct feedback, oldest first. */
export const EditHistory = ({ entityType, entityId, authorId }: Props) => {
  const query = useAuditLog(entityType, entityId);

  if (query.isPending) return <Skeleton active paragraph={{ rows: 3 }} />;
  if (query.isError) return <InlineError title="Couldn't load edit history" detail={query.error.message} />;

  const { entries, labels } = query.data;
  if (entries.length === 0) {
    return <BlankState title="No edits yet" hint="Changes made after submitting will appear here." />;
  }

  return (
    <ol className={styles.list}>
      {entries.map((entry, i) => (
        <EntryRow
          key={entry.id}
          entry={entry}
          labels={labels}
          isAdminEdit={entry.actor.id !== authorId && canEditAnySubmission(entry.actorRole)}
          isLast={i === entries.length - 1}
        />
      ))}
    </ol>
  );
};

type EntryRowProps = {
  entry: AuditLogEntry;
  labels: Record<string, string>;
  isAdminEdit: boolean;
  isLast: boolean;
};

const EntryRow = ({ entry, labels, isAdminEdit, isLast }: EntryRowProps) => {
  const isDelete = entry.action === AUDIT_ACTIONS.DELETE;

  return (
    <li className={styles.item}>
      <div className={styles.rail} aria-hidden>
        <UserAvatar size={28} avatarUrl={entry.actor.avatarUrl} />
        {isLast ? null : <span className={styles.line} />}
      </div>
      <div className={styles.body}>
        <div className={styles.head}>
          <span className={styles.who}>
            <Text strong>{entry.actor.displayName}</Text>
            <Text type="secondary">{isDelete ? 'deleted it' : 'edited'}</Text>
            {isAdminEdit ? <Tag color="orange">Admin edit</Tag> : null}
          </span>
          <Tooltip title={dayjs(entry.createdAt).format('MMM D, YYYY h:mm A')}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {dayjs(entry.createdAt).fromNow()}
            </Text>
          </Tooltip>
        </div>
        {isDelete ? null : (
          <dl className={styles.changes}>
            {Object.entries(entry.changes).map(([field, change]) => (
              <div key={field} className={styles.change}>
                <dt>{FIELD_LABELS[field] ?? field}</dt>
                <dd>
                  {LONG_TEXT_FIELDS.has(field) ? (
                    <>
                      <TextBlock label="Before" value={formatValue(change.from, labels)} />
                      <TextBlock label="After" value={formatValue(change.to, labels)} />
                    </>
                  ) : (
                    <span className={styles.inline}>
                      <Text delete type="secondary">
                        {formatValue(change.from, labels)}
                      </Text>
                      <span className={styles.arrow}>→</span>
                      <Text>{formatValue(change.to, labels)}</Text>
                    </span>
                  )}
                </dd>
              </div>
            ))}
          </dl>
        )}
      </div>
    </li>
  );
};

const TextBlock = ({ label, value }: { label: string; value: string }) => (
  <div className={styles.block}>
    <Text type="secondary" className={styles.blockLabel}>
      {label}
    </Text>
    <Paragraph ellipsis={{ rows: 3, expandable: 'collapsible' }} style={{ marginBottom: 0, whiteSpace: 'pre-wrap' }}>
      {value}
    </Paragraph>
  </div>
);

// Ids (recipient, category, core values) render as the names the API resolved for them.
const formatValue = (value: AuditValue, labels: Record<string, string>): string => {
  if (value === null) return '—';
  if (Array.isArray(value)) return value.length === 0 ? '—' : value.map((v) => labels[v] ?? v).join(', ');
  return labels[value] ?? (value || '—');
};
